import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Table, 
  Modal, 
  Form, 
  Alert, 
  Spinner, 
  Badge, 
  Row, 
  Col,
  InputGroup,
  FormControl,
  Dropdown,
  DropdownButton,
  ListGroup,
  Tooltip,
  OverlayTrigger
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faKey, 
  faPlus, 
  faCheck, 
  faTrash, 
  faSpinner, 
  faSearch,
  faCalendarAlt,
  faUser,
  faExclamationTriangle,
  faSort,
  faSortUp,
  faSortDown,
  faFilter,
  faCopy,
  faDownload,
  faList
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { db, auth } from '../../services/firebase';
import { 
  collection, 
  addDoc, 
  getDocs, 
  deleteDoc, 
  doc, 
  query, 
  where, 
  orderBy, 
  Timestamp,
  serverTimestamp,
  updateDoc,
  writeBatch
} from 'firebase/firestore';

/**
 * Componente per la gestione dei codici di attivazione (admin)
 */
const ActivationCodeManager = () => {
  const { t } = useTranslation();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [selectedDuration, setSelectedDuration] = useState(90); // 3 mesi default
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filterStatus, setFilterStatus] = useState('all');
  const [generatingCode, setGeneratingCode] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [codeToDelete, setCodeToDelete] = useState(null);
  const [deletingCode, setDeletingCode] = useState(false);
  const [codeQuantity, setCodeQuantity] = useState(1); // Numero di codici da generare
  const [generatedCodes, setGeneratedCodes] = useState([]); // Codici appena generati
  const [showGeneratedCodesModal, setShowGeneratedCodesModal] = useState(false); // Modal per visualizzare i codici generati
  
  // Durate predefinite in giorni
  const durations = {
    90: '3',   // 3 mesi
    180: '6',  // 6 mesi
    365: '12', // 1 anno
    730: '24', // 2 anni
    1095: '36' // 3 anni
  };

  // Carica i codici quando il componente viene montato
  useEffect(() => {
    loadCodes();
  }, [sortField, sortDirection, filterStatus]);

  // Funzione per caricare i codici
  const loadCodes = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Crea la query di base
      let codesQuery = collection(db, 'activationCodes');
      
      // Aggiungi ordinamento
      codesQuery = query(codesQuery, orderBy(sortField, sortDirection));
      
      // Applica filtro per stato
      if (filterStatus !== 'all') {
        if (filterStatus === 'available') {
          codesQuery = query(
            codesQuery, 
            where('isUsed', '==', false)
          );
        } else if (filterStatus === 'used') {
          codesQuery = query(codesQuery, where('isUsed', '==', true));
        }
      }
      
      const snapshot = await getDocs(codesQuery);
      const codesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Filtra per ricerca
      const filteredCodes = searchTerm 
        ? codesList.filter(code => 
            code.code.includes(searchTerm.toUpperCase()) ||
            (code.usedBy && code.usedBy.includes(searchTerm))
          )
        : codesList;
      
      setCodes(filteredCodes);
    } catch (error) {
      console.error('Errore nel caricamento dei codici:', error);
      setError(t('admin.activationCodes.loadError'));
    } finally {
      setLoading(false);
    }
  };

  // Funzione per generare un codice casuale
  const generateUniqueCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Esclusi caratteri simili (0/O, 1/I)
    let result = '';
    for (let i = 0; i < 12; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    // Inserisce trattini ogni 4 caratteri
    return result.replace(/(.{4})(.{4})(.{4})/, '$1-$2-$3');
  };

  // Funzione per creare un nuovo batch di codici
  const createCodes = async () => {
    try {
      setGeneratingCode(true);
      setError('');
      
      // Array per memorizzare i nuovi codici generati
      const newCodes = [];
      const batch = writeBatch(db);
      
      // Genera il numero specificato di codici
      for (let i = 0; i < codeQuantity; i++) {
        // Genera un codice casuale
        const codeString = generateUniqueCode();
        const cleanCode = codeString.replace(/-/g, '').toLowerCase();
        
        // Crea il nuovo codice senza data di scadenza
        const newCode = {
          code: codeString,
          duration: parseInt(selectedDuration),
          createdAt: serverTimestamp(),
          isUsed: false,
          usedBy: null,
          usedAt: null,
          plan: 'premium'
        };
        
        // Usa il codice come ID del documento (senza trattini)
        const docId = `code_${cleanCode}`;
        const codeDocRef = doc(db, 'activationCodes', docId);
        
        // Aggiungi al batch Firestore
        batch.set(codeDocRef, newCode);
        
        // Salva il codice nell'array locale per la visualizzazione
        newCodes.push({
          id: docId,
          ...newCode,
          code: codeString, // Assicurati che il codice sia disponibile per la visualizzazione
          createdAt: new Date() // Usa la data corrente per la visualizzazione locale
        });
      }
      
      // Esegui il batch write
      await batch.commit();
      
      // Aggiorna la lista dei codici
      setCodes(prevCodes => [...newCodes, ...prevCodes]);
      
      // Imposta i codici generati per la visualizzazione
      setGeneratedCodes(newCodes);
      
      // Mostra modal con i codici generati
      setShowGeneratedCodesModal(true);
      
      // Chiudi il modal di generazione
      setShowGenerateModal(false);
      
      // Mostra messaggio di successo
      setSuccessMessage(t('admin.activationCodes.generateSuccess'));
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      
    } catch (error) {
      console.error('Errore nella generazione dei codici:', error);
      setError(t('admin.activationCodes.generateError'));
    } finally {
      setGeneratingCode(false);
    }
  };
  
  // Funzione per copiare un codice negli appunti
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(
      () => {
        // Copia riuscita
        console.log('Codice copiato negli appunti');
      },
      (err) => {
        console.error('Errore durante la copia negli appunti:', err);
      }
    );
  };
  
  // Funzione per copiare tutti i codici negli appunti
  const copyAllCodes = () => {
    const textToCopy = generatedCodes.map(code => code.code).join('\n');
    navigator.clipboard.writeText(textToCopy).then(
      () => {
        // Copia riuscita
        console.log('Tutti i codici copiati negli appunti');
      },
      (err) => {
        console.error('Errore durante la copia negli appunti:', err);
      }
    );
  };
  
  // Funzione per esportare i codici come CSV
  const exportCodesAsCSV = () => {
    const headers = ['Codice', 'Durata', 'Data Creazione'];
    const csvContent = [
      // Intestazioni
      headers.join(','),
      // Righe di dati
      ...generatedCodes.map(code => {
        const duration = code.duration + ' ' + t('admin.activationCodes.days');
        const creationDate = new Date().toLocaleDateString('it-IT');
        return [code.code, duration, creationDate].join(',');
      })
    ].join('\n');
    
    // Crea un oggetto Blob con il contenuto CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    
    // Crea un elemento link per scaricare il file
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `codici_attivazione_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    
    // Aggiungi il link al DOM, clicca e rimuovi
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Funzione per eliminare un codice
  const deleteCode = async () => {
    if (!codeToDelete) return;
    
    try {
      setDeletingCode(true);
      
      // Elimina il codice da Firestore
      await deleteDoc(doc(db, 'activationCodes', codeToDelete.id));
      
      // Aggiorna la lista dei codici
      setCodes(prevCodes => prevCodes.filter(code => code.id !== codeToDelete.id));
      
      // Chiudi il modal di conferma
      setShowDeleteModal(false);
      setCodeToDelete(null);
      
      // Mostra messaggio di successo
      setSuccessMessage(t('admin.activationCodes.deleteSuccess'));
      setTimeout(() => setSuccessMessage(''), 3000);
      
    } catch (error) {
      console.error('Errore nell\'eliminazione del codice:', error);
      setError(t('admin.activationCodes.deleteError'));
    } finally {
      setDeletingCode(false);
    }
  };
  
  // Funzione per confermare l'eliminazione di un codice
  const confirmDelete = (code) => {
    setCodeToDelete(code);
    setShowDeleteModal(true);
  };
  
  // Cambia il campo e la direzione di ordinamento
  const handleSort = (field) => {
    if (field === sortField) {
      // Se il campo è già selezionato, inverti la direzione
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Altrimenti, imposta il nuovo campo e la direzione predefinita
      setSortField(field);
      setSortDirection('desc');
    }
  };
  
  // Ottieni l'icona di ordinamento
  const getSortIcon = (field) => {
    if (field !== sortField) return <FontAwesomeIcon icon={faSort} />;
    return sortDirection === 'asc' 
      ? <FontAwesomeIcon icon={faSortUp} /> 
      : <FontAwesomeIcon icon={faSortDown} />;
  };

  // Formatta la data
  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    try {
      // Se è un timestamp di Firestore, usa toDate()
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        return new Intl.DateTimeFormat('it-IT', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }).format(timestamp.toDate());
      }
      
      // Se è un oggetto Date, usalo direttamente
      if (timestamp instanceof Date) {
        return new Intl.DateTimeFormat('it-IT', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }).format(timestamp);
      }
      
      // Altrimenti, prova a creare un nuovo oggetto Date
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) {
        return '-'; // Se la data non è valida, ritorna un placeholder
      }
      
      return new Intl.DateTimeFormat('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (error) {
      console.error('Errore nella formattazione della data:', error);
      return '-';
    }
  };
  
  // Determina il colore del badge per lo stato
  const getStatusBadge = (code) => {
    if (code.isUsed) {
      return (
        <Badge bg="secondary">
          <FontAwesomeIcon icon={faCheck} className="me-1" />
          {t('admin.activationCodes.status.used')}
        </Badge>
      );
    } else {
      return (
        <Badge bg="success">
          <FontAwesomeIcon icon={faKey} className="me-1" />
          {t('admin.activationCodes.status.available')}
        </Badge>
      );
    }
  };

  return (
    <div className="activation-code-manager">
      <Card className="shadow-sm mb-4">
        <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <FontAwesomeIcon icon={faKey} className="me-2" />
            {t('admin.activationCodes.title')}
          </h5>
          <Button 
            variant="light" 
            size="sm" 
            onClick={() => setShowGenerateModal(true)}
          >
            <FontAwesomeIcon icon={faPlus} className="me-1" />
            {t('admin.activationCodes.generate')}
          </Button>
        </Card.Header>
        <Card.Body>
          <p className="text-muted mb-4">
            {t('admin.activationCodes.description')}
          </p>
          
          {error && (
            <Alert variant="danger" className="mb-3">
              {error}
            </Alert>
          )}
          
          {successMessage && (
            <Alert variant="success" className="mb-3">
              {successMessage}
            </Alert>
          )}
          
          <Row className="mb-3">
            <Col md={6}>
              <InputGroup>
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faSearch} />
                </InputGroup.Text>
                <FormControl
                  placeholder={t('admin.activationCodes.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <Button 
                    variant="outline-secondary" 
                    onClick={() => setSearchTerm('')}
                  >
                    &times;
                  </Button>
                )}
              </InputGroup>
            </Col>
            <Col md={6} className="d-flex justify-content-end">
              <DropdownButton 
                variant="outline-secondary" 
                title={
                  <>
                    <FontAwesomeIcon icon={faFilter} className="me-1" />
                    {filterStatus === 'all' 
                      ? t('common.all') 
                      : t(`admin.activationCodes.status.${filterStatus}`)}
                  </>
                }
                className="me-2"
              >
                <Dropdown.Item 
                  active={filterStatus === 'all'} 
                  onClick={() => setFilterStatus('all')}
                >
                  {t('common.all')}
                </Dropdown.Item>
                <Dropdown.Item 
                  active={filterStatus === 'available'} 
                  onClick={() => setFilterStatus('available')}
                >
                  {t('admin.activationCodes.status.available')}
                </Dropdown.Item>
                <Dropdown.Item 
                  active={filterStatus === 'used'} 
                  onClick={() => setFilterStatus('used')}
                >
                  {t('admin.activationCodes.status.used')}
                </Dropdown.Item>
              </DropdownButton>
              
              <Button 
                variant="outline-primary" 
                onClick={() => loadCodes()}
              >
                <FontAwesomeIcon 
                  icon={faSpinner} 
                  spin={loading} 
                  className="me-1" 
                />
                {loading ? t('common.loading') : t('common.refresh')}
              </Button>
            </Col>
          </Row>
          
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" role="status" />
              <p className="mt-2 text-muted">{t('common.loading')}</p>
            </div>
          ) : codes.length === 0 ? (
            <Alert variant="info">
              {searchTerm 
                ? t('common.noSearchResults') 
                : t('admin.activationCodes.noCodes')}
            </Alert>
          ) : (
            <div className="table-responsive">
              <Table hover className="activation-codes-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('code')} className="sortable">
                      {t('admin.activationCodes.code')} {getSortIcon('code')}
                    </th>
                    <th onClick={() => handleSort('duration')} className="sortable">
                      {t('admin.activationCodes.duration')} {getSortIcon('duration')}
                    </th>
                    <th onClick={() => handleSort('createdAt')} className="sortable">
                      {t('admin.activationCodes.createdAt')} {getSortIcon('createdAt')}
                    </th>
                    <th>
                      {t('admin.activationCodes.statusLabel')}
                    </th>
                    <th>
                      {t('admin.activationCodes.usedBy')}
                    </th>
                    <th>
                      {t('admin.activationCodes.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {codes.map(code => (
                    <tr key={code.id}>
                      <td>
                        <code>{code.code}</code>
                      </td>
                      <td>
                        {code.duration} {t('admin.activationCodes.days')}
                      </td>
                      <td>
                        <FontAwesomeIcon icon={faCalendarAlt} className="me-1 text-muted" />
                        {formatDate(code.createdAt)}
                      </td>
                      <td>
                        {getStatusBadge(code)}
                      </td>
                      <td>
                        {code.isUsed && code.usedBy ? (
                          <>
                            <FontAwesomeIcon icon={faUser} className="me-1 text-muted" />
                            {code.usedBy.substring(0, 8)}...
                          </>
                        ) : '-'}
                      </td>
                      <td>
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          onClick={() => confirmDelete(code)}
                          disabled={code.isUsed}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
      
      {/* Modal per generare nuovi codici */}
      <Modal 
        show={showGenerateModal} 
        onHide={() => setShowGenerateModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faKey} className="me-2" />
            {t('admin.activationCodes.generateTitle')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert variant="danger" className="mb-3">
              {error}
            </Alert>
          )}
          
          {successMessage && (
            <Alert variant="success" className="mb-3">
              {successMessage}
            </Alert>
          )}
          
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>{t('admin.activationCodes.monthsLabel')}</Form.Label>
              <Form.Select
                value={selectedDuration}
                onChange={(e) => setSelectedDuration(e.target.value)}
                disabled={generatingCode}
              >
                {Object.entries(durations).map(([days, months]) => (
                  <option key={days} value={days}>
                    {t(`admin.activationCodes.months.${months}`)}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                {t('admin.activationCodes.durationHelp')}
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>{t('admin.activationCodes.quantity')}</Form.Label>
              <Form.Control
                type="number"
                min="1"
                max="100"
                value={codeQuantity}
                onChange={(e) => setCodeQuantity(Math.min(100, Math.max(1, parseInt(e.target.value) || 1)))}
                disabled={generatingCode}
              />
              <Form.Text className="text-muted">
                {t('admin.activationCodes.quantityHelp')}
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowGenerateModal(false)}
            disabled={generatingCode}
          >
            {t('common.cancel')}
          </Button>
          <Button 
            variant="primary" 
            onClick={createCodes}
            disabled={generatingCode}
          >
            {generatingCode ? (
              <>
                <Spinner 
                  as="span" 
                  animation="border" 
                  size="sm" 
                  role="status" 
                  aria-hidden="true" 
                  className="me-2"
                />
                {t('common.loading')}
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faKey} className="me-2" />
                {t('admin.activationCodes.generate')}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Modal per visualizzare i codici generati */}
      <Modal
        show={showGeneratedCodesModal}
        onHide={() => setShowGeneratedCodesModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faList} className="me-2" />
            {t('admin.activationCodes.generatedCodes')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="success" className="mb-3">
            <FontAwesomeIcon icon={faCheck} className="me-2" />
            {t('admin.activationCodes.codesGeneratedSuccess')}
          </Alert>
          
          <div className="d-flex justify-content-end mb-3">
            <Button
              variant="outline-primary"
              className="me-2"
              onClick={copyAllCodes}
            >
              <FontAwesomeIcon icon={faCopy} className="me-2" />
              {t('admin.activationCodes.copyAll')}
            </Button>
            <Button
              variant="outline-success"
              onClick={exportCodesAsCSV}
            >
              <FontAwesomeIcon icon={faDownload} className="me-2" />
              {t('admin.activationCodes.exportCSV')}
            </Button>
          </div>
          
          <ListGroup>
            {generatedCodes.map((code, index) => (
              <ListGroup.Item
                key={index}
                className="d-flex justify-content-between align-items-center"
              >
                <code className="fs-5">{code.code}</code>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>{t('admin.activationCodes.copyTooltip')}</Tooltip>}
                >
                  <Button
                    variant="light"
                    size="sm"
                    onClick={() => copyToClipboard(code.code)}
                  >
                    <FontAwesomeIcon icon={faCopy} />
                  </Button>
                </OverlayTrigger>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={() => setShowGeneratedCodesModal(false)}
          >
            {t('common.close')}
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Modal per confermare l'eliminazione */}
      <Modal 
        show={showDeleteModal} 
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faTrash} className="me-2 text-danger" />
            {t('common.confirmDelete')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            {t('admin.activationCodes.confirmDelete')}
          </p>
          {codeToDelete && (
            <Alert variant="info">
              <strong>{t('admin.activationCodes.code')}:</strong> {codeToDelete.code}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowDeleteModal(false)}
            disabled={deletingCode}
          >
            {t('common.cancel')}
          </Button>
          <Button 
            variant="danger" 
            onClick={deleteCode}
            disabled={deletingCode}
          >
            {deletingCode ? (
              <>
                <Spinner 
                  as="span" 
                  animation="border" 
                  size="sm" 
                  role="status" 
                  aria-hidden="true" 
                  className="me-2"
                />
                {t('common.loading')}
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faTrash} className="me-2" />
                {t('common.delete')}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Stili CSS in-line */}
      <style>{`
        .activation-codes-table th.sortable {
          cursor: pointer;
        }
        .activation-codes-table th.sortable:hover {
          background-color: #f8f9fa;
        }
      `}</style>
    </div>
  );
};

export default ActivationCodeManager; 